import React from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import moment from 'moment';
import { Card } from 'tea-component';
import MetricsCardTrendChart from '@/pages/dashboard/components/MetricsCardTrendChart';
import MetricsCardValue from '@/pages/dashboard/components/MetricsCardValue';
import MetricsCardDod from '@/pages/dashboard/components/MetricsCardDod';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import './index.scss';

export const CasualCard: React.FC<{ children: React.ReactNode; className?: string; }> = ({
  children,
  className,
}) => (
  <Card className={classNames('dbh-casual-card', className)}>
    <Card.Body>{children}</Card.Body>
  </Card>
);

export const CasualCardHeader: React.FC<{
  titleEn: string;
  titleCn: string;
  subtitleEn?: string;
  subtitleCn?: string;
  titleIconUrl?: string;
  operation?: React.ReactNode;
}> = ({ titleEn, titleCn, subtitleEn, subtitleCn, titleIconUrl }) => {
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const title = getName(isEn, titleEn, titleCn);
  const subtitle = getName(isEn, subtitleEn, subtitleCn);

  return (
    <div className="dbh-casual-card__header">
      <div className="dbh-casual-card__title-row">
        <div className='dbh-casual-card__title-wrapper'>
          {titleIconUrl && (
            <img className="dbh-casual-card__title-icon" src={titleIconUrl} alt="" />
          )}
          <span className="dbh-casual-card__title">{title}</span>
        </div>
        {subtitle && <span className="dbh-casual-card__subtitle">{subtitle}</span>}
        {/* <div style={{ marginLeft: 'auto' }}>{operation}</div> */}
      </div>
    </div>
  );
};

export const CasualCardMetric: React.FC<{
  value: string | number | undefined;
  formatRule?: string;
  preUnit?: string;
  dodData: any[];
}> = ({ value, formatRule, preUnit, dodData }) => (
  <div className="dbh-casual-card__metric">
    <MetricsCardValue
      value={value ?? ''}
      formatRule={formatRule}
      preUnit={preUnit}
      className="dbh-casual-card__value"
    />
    <MetricsCardDod data={dodData} />
  </div>
);

export const CasualCardTrend: React.FC<{
  trendTitleEn?: string;
  trendTitleCn?: string;
  trendList: any[];
  componentMetrics: any[];
  isLeftCard?: boolean;
  isRightCard?: boolean;
  trendTimeType?: 'Month' | 'Day';
  /** 传入则与默认合并，用于柱形图等（如 showBarChart: true, showLineChart: false） */
  trendShowConfig?: {
    showBarChart?: boolean;
    showLineChart?: boolean;
    showBubbleTitle?: boolean;
    /** 多系列柱形图时堆叠（默认 false 为并列柱） */
    isStackBar?: boolean;
    /** 堆叠柱是否显示柱顶合计；默认 true（等同其它页面），设 false 关闭数据标签 */
    stackBarShowTotalLabel?: boolean;
  };
}> = ({
  trendTitleEn = 'Revenue Trend (6M)',
  trendTitleCn = 'Revenue Trend (6M)',
  trendList,
  componentMetrics,
  isLeftCard = false,
  isRightCard = false,
  trendTimeType = 'Month',
  trendShowConfig,
}) => {
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const trendTitle = getName(isEn, trendTitleEn, trendTitleCn);
  const showConfig = {
    showLineChart: true,
    showBubbleTitle: true,
    ...trendShowConfig,
  };

  const hasData = trendList?.some((item) =>
    componentMetrics.some((metric) => {
      const val = (item as Record<string, unknown>)?.metrics as Record<string, Record<string, unknown>> | undefined;
      const metricVal = val?.[metric.metricKey]?.value;
      return metricVal !== null && metricVal !== undefined && metricVal !== '';
    })
  );

  return (
    <div className="dbh-casual-card__trend">
      <div className="dbh-casual-card__trend-title">{trendTitle}</div>
      <MetricsCardTrendChart
        metricData={trendList}
        loading={false}
        error={false}
        filterVal={{ timeType: trendTimeType }}
        componentMetrics={componentMetrics}
        height={90}
        isLeftCard={isLeftCard}
        isRightCard={isRightCard}
        showConfig={showConfig}
        isStackBar={Boolean(trendShowConfig?.isStackBar)}
        stackBarShowTotalLabel={trendShowConfig?.stackBarShowTotalLabel ?? true}
      />
    </div>
  );
};

export const CasualCardBody: React.FC<{ children: React.ReactNode; }> = ({ children }) => (
  <div className="dbh-casual-card__body">{children}</div>
);

/**
 * Casual Overview：多月筛选（CasualOverviewWrap 中 is_confirm === true）与单月区分。
 * is_confirm 未传时按起止是否同月兜底。
 */
export const isCasualHubMultiMonthRange = (
  filterVal: Record<string, unknown> | undefined,
): boolean => {
  if (!filterVal) return false;
  if (filterVal.is_confirm === true) return true;
  if (filterVal.is_confirm === false) return false;
  const st = filterVal.start_time;
  const et = filterVal.end_time;
  if (st === undefined || st === null || et === undefined || et === null) {
    return false;
  }
  const s = String(st).trim().slice(0, 10);
  const e = String(et).trim().slice(0, 10);
  const ms = moment(s, 'YYYY-MM-DD', true);
  const me = moment(e, 'YYYY-MM-DD', true);
  if (!ms.isValid() || !me.isValid()) return false;
  return ms.month() !== me.month() || ms.year() !== me.year();
};

export interface ICompareMetricItem {
  dataKey: 'dod' | 'yoy' | 'change';
  titleEn: string;
  titleCn: string;
  when?: 'single' | 'multi';
  /** 浮窗 thisValue/lastValue 的格式化规则，默认 thousandsInit */
  extendFormatRule?: string;
}

/**
 * Utility to format DoD/YoY data for MetricsCardDod。
 * 优先使用 compareMetrics 配置驱动；未配置时走旧逻辑（isMultiMonthRange → change，否则 dod + yoy）兼容。
 */
export const getDodValues = (
  cardData: any,
  options?: {
    isMultiMonthRange?: boolean;
    compareMetrics?: ICompareMetricItem[];
  },
): any[] => {
  if (options?.compareMetrics?.length) {
    const isMulti = options?.isMultiMonthRange;
    const filtered = options.compareMetrics.filter((item) => {
      if (!item.when) return true;
      return item.when === 'single' ? !isMulti : isMulti;
    });
    const result: any[] = [];
    filtered.forEach((item) => {
      const src = cardData?.[item.dataKey];
      if (!src) return;
      result.push({
        titleEn: item.titleEn,
        titleCn: item.titleCn,
        value: src?.show_value || '',
        formatRule: 'absPercent1Decimal',
        extendFormatRule: item.extendFormatRule || 'thousandsInit',
        thisValue: src?.this_value || '',
        lastValue: src?.last_value || '',
        thisTime: src?.this_time || '',
        lastTime: src?.last_time || '',
      });
    });
    return result;
  }

  if (options?.isMultiMonthRange) {
    if (cardData?.change) {
      const ch = cardData.change;
      return [
        {
          titleEn: 'change',
          titleCn: '变化率',
          value: ch?.show_value || '',
          formatRule: 'absPercent1Decimal',
          extendFormatRule: 'thousandsInit',
          thisValue: ch?.this_value || '',
          lastValue: ch?.last_value || '',
          thisTime: ch?.this_time || '',
          lastTime: ch?.last_time || '',
        },
      ];
    }
    return [];
  }

  const dodValues: unknown[] = [];
  if (cardData?.dod) {
    const { dod } = cardData;
    dodValues.push({
      titleEn: 'MoM',
      titleCn: '月环比',
      value: dod?.show_value || '',
      formatRule: 'absPercent1Decimal',
      extendFormatRule: 'thousandsInit',
      thisValue: dod?.this_value || '',
      lastValue: dod?.last_value || '',
      thisTime: dod?.this_time || '',
      lastTime: dod?.last_time || '',
    });
  }
  if (cardData?.yoy) {
    const { yoy } = cardData;
    dodValues.push({
      titleEn: 'YoY',
      titleCn: '同比',
      value: yoy?.show_value || '',
      formatRule: 'absPercent1Decimal',
      extendFormatRule: 'thousandsInit',
      thisValue: yoy?.this_value || '',
      lastValue: yoy?.last_value || '',
      thisTime: yoy?.this_time || '',
      lastTime: yoy?.last_time || '',
    });
  }
  return dodValues;
};
